import { useCallback, useState, type ReactNode } from 'react';

import { Alert, Icons } from '@contiple/antd-ds';

import {
  ToastContext,
  type ToastContextValue,
  type ToastType,
} from './ToastContext';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

const AUTO_CLOSE_MS = 3000;

function getIcon(type: ToastType) {
  switch (type) {
    case 'success':
      return <Icons.CircleCheck size={16} />;
    case 'info':
      return <Icons.Info size={16} />;
    case 'warning':
      return <Icons.CircleAlert size={16} />;
    case 'error':
      return <Icons.CircleX size={16} />;
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (type: ToastType, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => remove(id), AUTO_CLOSE_MS);
    },
    [remove]
  );

  const value: ToastContextValue = {
    show,
    success: (msg) => show('success', msg),
    info: (msg) => show('info', msg),
    warning: (msg) => show('warning', msg),
    error: (msg) => show('error', msg),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
        {toasts.map((toast) => (
          <Alert
            key={toast.id}
            type={toast.type}
            title={toast.message}
            showIcon
            icon={getIcon(toast.type)}
            closable
            onClose={() => remove(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
